import { Popup } from 'components/Popup'
import { Typography } from 'components/Typography'
import { useBankAccountPopup } from './Providers/BankAccountPopupProvider'
import { BUTTON_TYPES, Button } from 'components/Button'
import { BTN_TXT, NOTIFICATIONS, PLACEHOLDERS } from 'constants/txt'
import { useCallback, useEffect, useMemo } from 'react'
import { setLoading } from 'redux/store/common/slice'
import { useDispatch } from 'react-redux'
import { Form, notification } from 'antd'
import { Select } from 'components/Select'
import { formatBankAccounName } from './helpers'
import { deleteBankAccountDetails, updateBankAccountDetails } from './api'
import { CustomForm } from 'components/Forms/CustomForm'
import { validateMessages } from 'helper/common'
import { getDefaultReceivablePopupTitle } from './constants'

const FORM_ID = 'set-new-default-receivable-account-form'

type TFormValues = {
  default_bank_account: string
}

export const SetNewDefaultReceivableAccount = ({
  onSuccess
}: {
  onSuccess: () => void
}) => {
  const {
    setAnotherDefaultReceivableAccountPopup: { state, actions }
  } = useBankAccountPopup()

  const {
    name,
    uuid,
    business_account_id,
    listOfAvailableAccountsToSetAsDefault,
    action = 'delete',
    formData,
    onSuccess: onSuccessFromPayload
  } = state.payload

  const isChangePurpose = action === 'changePurpose'

  const dispatch = useDispatch()
  const [form] = Form.useForm<TFormValues>()

  const options = useMemo(
    () =>
      listOfAvailableAccountsToSetAsDefault?.map((i) => ({
        label: formatBankAccounName(i),
        subtitle: i?.bank_name,
        value: i?.uuid
      })) ?? [],
    [listOfAvailableAccountsToSetAsDefault]
  )

  const firstOptionValue = options[0]?.value

  useEffect(() => {
    if (state.visible && firstOptionValue) {
      form.setFieldsValue({ default_bank_account: firstOptionValue })
    }
  }, [state.visible, firstOptionValue, form])

  const setNewDefaultReceivableAccount = useCallback(
    async ({ default_bank_account }: TFormValues) => {
      if (!uuid || !business_account_id) return
      try {
        dispatch(setLoading(true))
        if (isChangePurpose) {
          await updateBankAccountDetails(business_account_id, uuid, {
            ...formData,
            new_default_bank_account_id: default_bank_account,
            is_default: false
          })
          onSuccessFromPayload?.()
        } else {
          await deleteBankAccountDetails(uuid, business_account_id, {
            new_default_bank_account_id: default_bank_account
          })
          onSuccess()
        }
      } catch (error) {
        console.error(error)
        notification.error({
          message: NOTIFICATIONS.SOMETHING_WENT_WRONG
        })
      } finally {
        dispatch(setLoading(false))
        actions.close()
      }
    },
    [
      uuid,
      business_account_id,
      dispatch,
      isChangePurpose,
      formData,
      onSuccessFromPayload,
      onSuccess,
      actions
    ]
  )

  return (
    <Popup
      {...state}
      title={getDefaultReceivablePopupTitle('setAnotherDefault', action)}
      zIndex={10000}
      width={442}
      onCancel={actions.close}
      footer={
        <>
          <Button type={BUTTON_TYPES.DEFAULT} onClick={actions.close} upperCase>
            {BTN_TXT.CANCEL}
          </Button>
          <Button
            type={BUTTON_TYPES.PRIMARY}
            htmlType="submit"
            form={FORM_ID}
            upperCase
            danger={!isChangePurpose}
          >
            {isChangePurpose ? BTN_TXT.SAVE : BTN_TXT.CONFIRM_AND_DELETE}
          </Button>
        </>
      }
    >
      <div className="column gap-24">
        <Typography.Body2 className="grey-text">
          {isChangePurpose
            ? `Select which account should receive payments after ${name} is updated.`
            : `Select which account should receive payments after ${name} is deleted.`}
        </Typography.Body2>
        <CustomForm<TFormValues>
          form={form}
          id={FORM_ID}
          onFinish={setNewDefaultReceivableAccount}
          validateMessages={validateMessages}
        >
          <Select.WithSubtitle
            options={options}
            propsItem={{
              name: 'default_bank_account',
              rules: [
                {
                  required: true
                }
              ]
            }}
            propsSelect={{
              placeholder: PLACEHOLDERS.PLEASE_SELECT
            }}
          />
        </CustomForm>
      </div>
    </Popup>
  )
}
